import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../shared/Card';
import { ArrowRightIcon, MapPinIcon, BookIcon, HomeIcon, BriefcaseIcon } from 'lucide-react';
type ContextCaptureProps = {
  onComplete: () => void;
};
export const ContextCapture: React.FC<ContextCaptureProps> = ({
  onComplete
}) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showLifeMap, setShowLifeMap] = useState(false);
  const sections = [{
    id: 'family',
    title: 'Family Background',
    icon: <HomeIcon size={24} data-id="element-963" />,
    questions: [{
      id: 'family_structure',
      text: 'What best describes your family structure growing up?',
      options: [{
        value: 'nuclear',
        label: 'Nuclear family',
        icon: '👨‍👩‍👧‍👦'
      }, {
        value: 'single_parent',
        label: 'Single parent',
        icon: '👩‍👧'
      }, {
        value: 'extended',
        label: 'Extended family',
        icon: '👨‍👩‍👧‍👦👵👴'
      }, {
        value: 'blended',
        label: 'Blended family',
        icon: '👨‍👩‍👧‍👦👨‍👩‍👦'
      }]
    }, {
      id: 'birth_order',
      text: 'What is your birth order?',
      options: [{
        value: 'oldest',
        label: 'Oldest child',
        icon: '1️⃣'
      }, {
        value: 'middle',
        label: 'Middle child',
        icon: '2️⃣'
      }, {
        value: 'youngest',
        label: 'Youngest child',
        icon: '3️⃣'
      }, {
        value: 'only',
        label: 'Only child',
        icon: '🧒'
      }]
    }]
  }, {
    id: 'geography',
    title: 'Geographic Context',
    icon: <MapPinIcon size={24} data-id="element-964" />,
    questions: [{
      id: 'environment',
      text: 'What type of environment did you grow up in?',
      options: [{
        value: 'urban',
        label: 'Urban city',
        icon: '🏙️'
      }, {
        value: 'suburban',
        label: 'Suburban area',
        icon: '🏘️'
      }, {
        value: 'rural',
        label: 'Rural community',
        icon: '🌄'
      }, {
        value: 'mixed',
        label: 'Mixed/Multiple',
        icon: '🏙️🌄'
      }]
    }, {
      id: 'relocations',
      text: 'How many times have you relocated in your life?',
      options: [{
        value: 'none',
        label: 'Never moved',
        icon: '🏠'
      }, {
        value: 'few',
        label: '1-3 times',
        icon: '🚚'
      }, {
        value: 'several',
        label: '4-10 times',
        icon: '✈️'
      }, {
        value: 'many',
        label: 'More than 10 times',
        icon: '🌎'
      }]
    }]
  }, {
    id: 'education',
    title: 'Educational Background',
    icon: <BookIcon size={24} data-id="element-965" />,
    questions: [{
      id: 'education_level',
      text: 'What is your highest level of education?',
      options: [{
        value: 'high_school',
        label: 'High School',
        icon: '🎓'
      }, {
        value: 'associates',
        label: "Associate's Degree",
        icon: '📜'
      }, {
        value: 'bachelors',
        label: "Bachelor's Degree",
        icon: '🎓'
      }, {
        value: 'graduate',
        label: 'Graduate Degree',
        icon: '👨‍🎓'
      }]
    }, {
      id: 'learning_style',
      text: 'How would you describe your learning style?',
      options: [{
        value: 'visual',
        label: 'Visual learner',
        icon: '👁️'
      }, {
        value: 'auditory',
        label: 'Auditory learner',
        icon: '👂'
      }, {
        value: 'reading',
        label: 'Reading/Writing',
        icon: '📝'
      }, {
        value: 'kinesthetic',
        label: 'Hands-on learner',
        icon: '🙌'
      }]
    }]
  }, {
    id: 'work',
    title: 'Work Experience',
    icon: <BriefcaseIcon size={24} data-id="element-966" />,
    questions: [{
      id: 'work_history',
      text: 'How would you describe your work history?',
      options: [{
        value: 'consistent',
        label: 'Consistent path',
        icon: '📈'
      }, {
        value: 'varied',
        label: 'Varied experiences',
        icon: '🔄'
      }, {
        value: 'specialized',
        label: 'Highly specialized',
        icon: '🔬'
      }, {
        value: 'entrepreneurial',
        label: 'Entrepreneurial',
        icon: '💼'
      }]
    }, {
      id: 'work_environment',
      text: 'What work environment do you prefer?',
      options: [{
        value: 'office',
        label: 'Traditional office',
        icon: '🏢'
      }, {
        value: 'remote',
        label: 'Remote work',
        icon: '🏠'
      }, {
        value: 'hybrid',
        label: 'Hybrid approach',
        icon: '🏢🏠'
      }, {
        value: 'field',
        label: 'Field/On-site work',
        icon: '🌎'
      }]
    }]
  }];
  const handleAnswer = (questionId: string, value: string) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };
  const currentSectionData = sections[currentSection];
  const allQuestionsAnswered = currentSectionData.questions.every(q => answers[q.id]);
  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    } else {
      setShowLifeMap(true);
      setTimeout(() => {
        onComplete();
      }, 5000);
    }
  };
  const getOptionIcon = (questionId: string, value: string) => {
    const question = sections.flatMap(s => s.questions).find(q => q.id === questionId);
    return question?.options.find(o => o.value === value)?.icon || '';
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="max-w-4xl mx-auto p-4" data-id="element-967">
      <div className="mb-8" data-id="element-968">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-969">
          Context Capture
        </h1>
        <p className="text-[#B8C0E0]" data-id="element-970">
          Tell us about your background to help us understand your career
          context
        </p>
      </div>
      {showLifeMap ? <LifeMapVisualization answers={answers} getOptionIcon={getOptionIcon} onComplete={onComplete} data-id="element-971" /> : <>
          <div className="flex mb-8 overflow-x-auto py-2" data-id="element-972">
            {sections.map((section, index) => <button key={section.id} onClick={() => setCurrentSection(index)} className={`flex items-center px-4 py-2 mr-2 rounded-full whitespace-nowrap ${index === currentSection ? 'bg-[#7D56F4] text-white' : 'bg-[#1C2541] text-[#B8C0E0]'}`} data-id="element-973">
                <span className="mr-2" data-id="element-974">{section.icon}</span>
                <span data-id="element-975">{section.title}</span>
              </button>)}
          </div>
          <Card data-id="element-976">
            <h2 className="text-xl font-bold mb-6 flex items-center" data-id="element-977">
              <span className="mr-3" data-id="element-978">{currentSectionData.icon}</span>
              {currentSectionData.title}
            </h2>
            <div className="space-y-8" data-id="element-979">
              {currentSectionData.questions.map(question => <div key={question.id} className="space-y-4" data-id="element-980">
                  <h3 className="font-medium" data-id="element-981">{question.text}</h3>
                  <div className="grid grid-cols-2 gap-3" data-id="element-982">
                    {question.options.map(option => <motion.button key={option.value} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => handleAnswer(question.id, option.value)} className={`p-4 rounded-lg flex items-center ${answers[question.id] === option.value ? 'bg-[#7D56F4] text-white' : 'bg-[#1C2541] text-[#B8C0E0] hover:bg-[#2C3555]'}`} data-id="element-983">
                        <div className="text-2xl mr-3" data-id="element-984">{option.icon}</div>
                        <span data-id="element-985">{option.label}</span>
                      </motion.button>)}
                  </div>
                </div>)}
            </div>
            <div className="mt-8 flex justify-end" data-id="element-986">
              <button onClick={nextSection} disabled={!allQuestionsAnswered} className={`py-2 px-6 rounded-full flex items-center ${allQuestionsAnswered ? 'bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white' : 'bg-[#1C2541] text-[#B8C0E0]'}`} data-id="element-987">
                {currentSection === sections.length - 1 ? 'Complete' : 'Next'}
                <ArrowRightIcon size={16} className="ml-2" data-id="element-988" />
              </button>
            </div>
          </Card>
          <div className="mt-4 flex justify-between items-center" data-id="element-989">
            <div className="text-sm text-[#B8C0E0]" data-id="element-990">
              Section {currentSection + 1} of {sections.length}
            </div>
            <div className="text-sm text-[#B8C0E0]" data-id="element-991">
              {Object.keys(answers).length} of{' '}
              {sections.reduce((acc, section) => acc + section.questions.length, 0)}{' '}
              questions answered
            </div>
          </div>
        </>}
    </motion.div>;
};
type LifeMapVisualizationProps = {
  answers: Record<string, string>;
  getOptionIcon: (questionId: string, value: string) => string;
  onComplete: () => void;
};
const LifeMapVisualization: React.FC<LifeMapVisualizationProps> = ({
  answers,
  getOptionIcon,
  onComplete
}) => {
  const [xpEarned, setXpEarned] = useState(0);
  useEffect(() => {
    // Simulate XP counting up
    const timer = setTimeout(() => {
      setXpEarned(500);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} data-id="element-992">
      <Card className="relative overflow-hidden" data-id="element-993">
        <h2 className="text-xl font-bold mb-6 text-center" data-id="element-994">Your Life Map</h2>
        {/* The visualization */}
        <div className="relative h-80 bg-[#0A1128] rounded-lg overflow-hidden mb-6" data-id="element-995">
          {/* Background elements */}
          <div className="absolute inset-0" data-id="element-996">
            <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 border-2 border-dashed border-[#3A506B] rounded-full" data-id="element-997"></div>
            <div className="absolute top-1/3 left-1/3 w-1/3 h-1/3 border-2 border-dashed border-[#3A506B] rounded-full" data-id="element-998"></div>
            <div className="absolute top-[45%] left-[45%] w-[10%] h-[10%] border-2 border-dashed border-[#3A506B] rounded-full" data-id="element-999"></div>
          </div>
          {/* Family icon */}
          {answers.family_structure && <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 0.5,
          duration: 0.5
        }} className="absolute top-[20%] left-[20%] bg-[#1C2541] p-3 rounded-full" data-id="element-1000">
              <div className="text-2xl" data-id="element-1001">
                {getOptionIcon('family_structure', answers.family_structure)}
              </div>
            </motion.div>}
          {/* Geography icon */}
          {answers.environment && <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 1,
          duration: 0.5
        }} className="absolute top-[30%] right-[20%] bg-[#1C2541] p-3 rounded-full" data-id="element-1002">
              <div className="text-2xl" data-id="element-1003">
                {getOptionIcon('environment', answers.environment)}
              </div>
            </motion.div>}
          {/* Education icon */}
          {answers.education_level && <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 1.5,
          duration: 0.5
        }} className="absolute bottom-[30%] left-[25%] bg-[#1C2541] p-3 rounded-full" data-id="element-1004">
              <div className="text-2xl" data-id="element-1005">
                {getOptionIcon('education_level', answers.education_level)}
              </div>
            </motion.div>}
          {/* Work icon */}
          {answers.work_history && <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 2,
          duration: 0.5
        }} className="absolute bottom-[20%] right-[25%] bg-[#1C2541] p-3 rounded-full" data-id="element-1006">
              <div className="text-2xl" data-id="element-1007">
                {getOptionIcon('work_history', answers.work_history)}
              </div>
            </motion.div>}
          {/* Central YOU icon */}
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 2.5,
          duration: 0.8
        }} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] p-4 rounded-full" data-id="element-1008">
            <div className="text-3xl" data-id="element-1009">😎</div>
          </motion.div>
          {/* Connection lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-1010">
            {answers.family_structure && <motion.line initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 1
          }} transition={{
            delay: 2.6,
            duration: 0.8
          }} x1="20%" y1="20%" x2="50%" y2="50%" stroke="#3A506B" strokeWidth="2" strokeDasharray="5,5" data-id="element-1011" />}
            {answers.environment && <motion.line initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 1
          }} transition={{
            delay: 2.7,
            duration: 0.8
          }} x1="80%" y1="30%" x2="50%" y2="50%" stroke="#3A506B" strokeWidth="2" strokeDasharray="5,5" data-id="element-1012" />}
            {answers.education_level && <motion.line initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 1
          }} transition={{
            delay: 2.8,
            duration: 0.8
          }} x1="25%" y1="70%" x2="50%" y2="50%" stroke="#3A506B" strokeWidth="2" strokeDasharray="5,5" data-id="element-1013" />}
            {answers.work_history && <motion.line initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 1
          }} transition={{
            delay: 2.9,
            duration: 0.8
          }} x1="75%" y1="80%" x2="50%" y2="50%" stroke="#3A506B" strokeWidth="2" strokeDasharray="5,5" data-id="element-1014" />}
          </svg>
        </div>
        {/* XP reward animation */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 3,
        duration: 0.5
      }} className="text-center mb-6" data-id="element-1015">
          <div className="text-2xl font-bold mb-2" data-id="element-1016">
            Context Capture Complete!
          </div>
          <div className="flex items-center justify-center" data-id="element-1017">
            <span className="text-[#FFD700] text-2xl mr-2" data-id="element-1018">✨</span>
            <motion.span initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 3.5,
            duration: 0.5
          }} className="text-xl font-bold text-[#FFD700]" data-id="element-1019">
              +{xpEarned} XP
            </motion.span>
          </div>
        </motion.div>
        <div className="flex justify-center" data-id="element-1020">
          <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 4,
          duration: 0.5
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={onComplete} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-3 px-8 rounded-full flex items-center" data-id="element-1021">
            <span data-id="element-1022">Continue</span>
            <ArrowRightIcon size={18} className="ml-2" data-id="element-1023" />
          </motion.button>
        </div>
      </Card>
    </motion.div>;
};